import React from "react";
import clsx from "clsx";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useStyles } from "./AdminSidebar.styles";
import { AssignmentIcon, DescriptionIcon } from "../icons";
import { SvgIconComponent } from "@mui/icons-material";
import DashboardRounded from "@mui/icons-material/DashboardRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import PersonSearchRounded from "@mui/icons-material/PersonSearchRounded";
import ContactsRounded from "@mui/icons-material/ContactsRounded";
import SupportAgentRounded from "@mui/icons-material/SupportAgentRounded";
import AutorenewRounded from "@mui/icons-material/AutorenewRounded";
import AssessmentRounded from "@mui/icons-material/AssessmentRounded";
import BusinessRounded from "@mui/icons-material/BusinessRounded";
import BusinessCenterRounded from "@mui/icons-material/BusinessCenterRounded";
import PeopleRounded from "@mui/icons-material/PeopleRounded";
import MapRounded from "@mui/icons-material/MapRounded";
import VerifiedUserRounded from "@mui/icons-material/VerifiedUserRounded";
import ListAltRounded from "@mui/icons-material/ListAltRounded";
import DataUsageRounded from "@mui/icons-material/DataUsageRounded";
import QuestionAnswerRounded from "@mui/icons-material/QuestionAnswerRounded";
import SubjectRounded from "@mui/icons-material/SubjectRounded";
import SettingsRounded from "@mui/icons-material/SettingsRounded";
import AdminPages, { resolveActiveMenuKey } from "../../pages/admin";
import { history } from "../../lib";
import {
  adminSelectors,
  authSelectors,
  commercialActions,
  Page,
  uiActions,
  uiSelectors,
  useDispatch,
  useSelector,
} from "../../state";
import { useLocation } from "react-router-dom";
import { ListItemButton } from "@mui/material";
import { useMobile } from "../../themes";

const Icon = (props) => {
  const { icon } = props;
  return React.createElement(icon);
};

// Rounded (solid) icon variant per menu key, used when the item is the
// active route. Outlined variants live on `AdminPages[key].icon` and are
// used when inactive — matching the same pattern as `AdminBottomNav`.
const ACTIVE_ICONS: Record<string, SvgIconComponent> = {
  dashboard: DashboardRounded,
  applications: DescriptionRounded,
  leads: PersonSearchRounded,
  udd: ContactsRounded,
  serviceRequests: SupportAgentRounded,
  referrals: AutorenewRounded,
  agentStats: AssessmentRounded,
  agentLink: BusinessRounded,
  agencies: BusinessCenterRounded,
  users: PeopleRounded,
  userAgencies: PeopleRounded,
  states: MapRounded,
  carriers: BusinessRounded,
  viewAllCarriers: MapRounded,
  coverage: VerifiedUserRounded,
  products: ListAltRounded,
  reports: DataUsageRounded,
  analytics: AssessmentRounded,
  faqs: QuestionAnswerRounded,
  coverageUI: SubjectRounded,
  settings: SettingsRounded,
};

const adminPage = (key: string): Page => {
  return AdminPages[key];
};
const DocLink = ({ onClick }: { onClick?: () => void }) => {
  const classes = useStyles();
  const { google_doc_url } = useSelector(adminSelectors.activeAgencyData);

  return google_doc_url ? (
    <ListItemButton
      key={"doc"}
      className={classes.listItem}
      component="a"
      href={google_doc_url}
      target="_blank"
      rel="noreferrer"
      onClick={onClick}
    >
      <ListItemIcon>
        <Icon icon={AssignmentIcon} />
      </ListItemIcon>
      <ListItemText primary="Lead management" />
    </ListItemButton>
  ) : null;
};
export const AdminSidebar = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const isMobile = useMobile("md");
  const activeRole = useSelector(authSelectors.activeRole);
  const adminMenuOpen = useSelector(uiSelectors.adminMenuOpen);
  const hide =
    activeRole === "agent" ||
    activeRole === "affiliate_agent" ||
    activeRole === "referral_product_owner";
  const checkRoles = (key: string): boolean => {
    if (activeRole === "super_admin") {
      return true;
    }

    if (adminPage(key).allowedRoles === undefined) {
      //No Roles on page allow everyone
      return true;
    }
    if (!activeRole) {
      return false;
    }
    return !!adminPage(key).allowedRoles?.includes(activeRole);
  };
  const handleDrawerOpen = () => {
    dispatch(uiActions.setAdminMenuOpen(true));
  };

  const handleDrawerClose = () => {
    dispatch(uiActions.setAdminMenuOpen(false));
  };

  const closeOnMobile = () => {
    if (isMobile) {
      handleDrawerClose();
    }
  };

  const activeMenuKey = resolveActiveMenuKey(location.pathname);

  return (
    <div className={classes.root}>
      <Drawer
        variant="permanent"
        onMouseEnter={handleDrawerOpen}
        onMouseLeave={handleDrawerClose}
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: adminMenuOpen,
          [classes.drawerClose]: !adminMenuOpen,
        })}
        classes={{
          paper: clsx({
            [classes.drawerOpen]: adminMenuOpen,
            [classes.drawerClose]: !adminMenuOpen,
          }),
        }}
      >
        <List>
          {Object.keys(AdminPages)
            .filter((key) => adminPage(key).menuItemOrder)
            .filter(checkRoles)
            .sort(
              (a, b) =>
                (adminPage(a).menuItemOrder ?? 0) -
                (adminPage(b)?.menuItemOrder ?? 0),
            )
            .map((p, index) => {
              const page: Page = AdminPages[p];
              const isActive =
                activeMenuKey === p || location.pathname === page.path;
              const isExternalLink = page.type.includes("Link");
              const linkProps = isExternalLink
                ? {
                    component: "a" as const,
                    href: page.path,
                    target: "_blank",
                    rel: "noreferrer",
                    onClick: closeOnMobile,
                  }
                : {
                    onClick: () => {
                      page.type === "Commercial"
                        ? dispatch(commercialActions.getIbyndSSORedirectLink())
                        : history.push(page.path);
                      closeOnMobile();
                    },
                  };
              return (
                <ListItemButton
                  {...linkProps}
                  key={index}
                  className={classes.listItem}
                >
                  <ListItemIcon className={isActive ? "active" : ""}>
                    <Icon
                      icon={
                        isActive
                          ? ACTIVE_ICONS[p] ?? page.icon ?? DescriptionRounded
                          : page.icon ?? DescriptionIcon
                      }
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={page.title}
                    className={isActive ? "active" : ""}
                  />
                </ListItemButton>
              );
            })}
          {!hide && <DocLink onClick={closeOnMobile} />}
        </List>
      </Drawer>
      <span className={classes.sideRailRightBorder} aria-hidden="true" />
      <span className={classes.cornerCurve} aria-hidden="true" />
    </div>
  );
};
